import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { DateFormatOptions } from '@jsverse/transloco-locale';
import dayjs, { Dayjs, OpUnitType } from 'dayjs';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import utc from 'dayjs/plugin/utc';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

dayjs.extend(utc);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export type DateInput = Date | Dayjs | string;

@Injectable({
	providedIn: 'root',
})
export class DateUtil {
	static minDate = dayjs.utc('1970-01-01');
	static maxDate = dayjs.utc('9999-12-31');

	/** True if `day` lies within [from, to] (inclusive, day granularity). Open bounds via min/max sentinels. */
	public static isValidOn(day: DateInput, from: DateInput | null | undefined, to: DateInput | null | undefined): boolean {
		const d = dayjs(day).startOf('day');
		const start = from == null ? DateUtil.minDate : dayjs(from).startOf('day');
		const end = to == null ? DateUtil.maxDate : dayjs(to).startOf('day');
		return d.isSameOrAfter(start) && d.isSameOrBefore(end);
	}

	/** True if a period is valid today (from ≤ today ≤ to), open bounds tolerated. */
	public static isValidToday(from: DateInput | null | undefined, to: DateInput | null | undefined): boolean {
		return DateUtil.isValidOn(dayjs(), from, to);
	}

	/** Classifies a period relative to today (past / today / future). */
	public static classifyPeriod(from: DateInput | null | undefined, to: DateInput | null | undefined): 'past' | 'today' | 'future' {
		const today = dayjs().startOf('day');
		const start = from == null ? DateUtil.minDate : dayjs(from).startOf('day');
		const end = to == null ? DateUtil.maxDate : dayjs(to).startOf('day');
		if (today.isBefore(start)) {
			return 'future';
		}
		if (today.isAfter(end)) {
			return 'past';
		}
		return 'today';
	}
	public static DATE_FORMATS = {
		parse: {
			dateInput: ['DD.MM.YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD', 'DDMMYYYY', 'DDMMYY'],
		},
		display: {
			dateInput: 'DD.MM.YYYY',
			monthYearLabel: 'MMM YYYY',
			dateA11yLabel: 'DD.MM.YYYY',
			monthYearA11yLabel: 'MMMM YYYY',
		},
	};

	public static readonly shortDateOptions: DateFormatOptions = {
		day: 'numeric', // e.g., '1', '15'
		month: 'numeric', // e.g., '1', '12'
		year: '2-digit', // e.g., '24', '25'
	};

	public static readonly localizedNumericDateOptions: DateFormatOptions = {
		day: '2-digit', // e.g., '01', '15'
		month: '2-digit', // e.g., '01', '12'
		year: 'numeric', // e.g., '2024', '2025'
	};

	private translocoService: TranslocoService;

	constructor() {
		const translocoService = inject(TranslocoService);

		this.translocoService = translocoService;
	}

	getDateWithoutTimezone(date: Date | Dayjs | string): Date {
		const dateToParse = dayjs(date).toDate();
		const userTimezoneOffset = dateToParse.getTimezoneOffset() * 60000;
		return new Date(dateToParse.getTime() - userTimezoneOffset);
	}

	checkDateValid(date: Date | Dayjs | string): string {
		const parsedDate = dayjs(date);
		const dateString = this.getLocaleDateFormatString();
		if (!parsedDate.isSame(dayjs('1970-01-01'), 'day') && !parsedDate.isSame(dayjs('9999-12-31'), 'day')) {
			return parsedDate.format(dateString);
		}
		return '\u00A0'; //empty string caused styling problems in roleView
	}

	setDateFormatByActiveLang(dateToParse: string | Date | Dayjs, dateFallback: string | Date | Dayjs, withHours = false, keepLocalTime = true) {
		return this.setDateTimeFormatByActiveLang(dateToParse, dateFallback, withHours, withHours, withHours, keepLocalTime);
	}

	setDateTimeFormatByActiveLang(
		dateToParse: string | Date | Dayjs,
		dateFallback: string | Date | Dayjs,
		withHours = true,
		withMinutes = true,
		withSeconds = false,
		keepLocalTime = true
	) {
		const parsedDate = dayjs(dateToParse);

		const datesToFallBack = [dayjs('1970-01-01'), dayjs('9999-12-31')];

		// datesToFallBack.includes() won't work reliably because of additional time values
		const dateToFallBack = datesToFallBack.find((dateFB) => dateFB.isSame(parsedDate, 'day'));
		if (dateToFallBack) {
			if (dateToFallBack.isSame(parsedDate, 'day')) {
				const fallbackDate = dayjs(dateFallback);
				if (!fallbackDate.isValid()) {
					return typeof dateFallback === 'string' ? dateFallback : '';
				}
			}
			return this.formatDate(parsedDate, withHours, keepLocalTime);
		}
		const date = dayjs(dateToParse ? dateToParse : dateFallback);
		if (!date.isValid()) {
			return '';
		}
		return this.formatDate(parsedDate, withHours, withMinutes, withSeconds, keepLocalTime);
	}

	dateMinimumFCValidator(date: string): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			if (control.value == null) {
				return null;
			}

			const controlDate = dayjs(control.value, date);

			if (!controlDate.isValid()) {
				return null;
			}

			const validationDate = dayjs(date);

			return !controlDate.isBefore(validationDate)
				? null
				: {
						'date-minimum': {
							'date-minimum': validationDate.format(date),
							actual: controlDate.format(date),
						},
					};
		};
	}

	dateRange(fromFieldName: string = 'validFrom', toFieldName: string = 'validTo', unit?: OpUnitType): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const fromValue = control.get(fromFieldName)?.value;
			const toValue = control.get(toFieldName)?.value;

			if (!fromValue || !toValue) {
				return null;
			}

			const fromDate = dayjs(fromValue);
			const toDate = dayjs(toValue);

			if (fromDate.isValid() && toDate.isValid() && !fromDate.isBefore(toDate, unit)) {
				return {
					dateRangeInvalid: {
						fromField: fromFieldName,
						toField: toFieldName,
						fromValue: fromValue,
						toValue: toValue,
					},
				};
			}

			return null;
		};
	}

	public formatDate(date: Dayjs, withHours, withMinutes = true, withSeconds = true, keepLocalTime = true) {
		const dateToFormat = dayjs(date).utc(keepLocalTime);
		const dateString = this.getLocaleDateFormatString();

		return withHours
			? withMinutes
				? withSeconds
					? dateToFormat.format(dateString + ' HH:mm:ss')
					: dateToFormat.format(dateString + ' HH:mm')
				: dateToFormat.format(dateString + ' HH')
			: dateToFormat.format(dateString);
	}

	public getLocaleDateFormatString() {
		const activeLang = this.translocoService.getActiveLang();
		let dateString = 'YYYY-MM-DD';
		if (activeLang === 'de') {
			dateString = 'DD.MM.YYYY';
		}
		return dateString;
	}

	public compareDates(date1: Date, date2: Date): boolean {
		return dayjs(date1).format('DD.MM.YYYY HH:mm:ss') === dayjs(date2).format('DD.MM.YYYY HH:mm:ss');
	}
}
